import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { tr } from '../../i18n/i18n';
import {
  IAutosaveExamAttemptAnswerPayload,
  IExamAttemptAutosaveResponse,
  IExamAttemptErrorResponse,
  IExamAttemptJoinResponse,
  IExamAttemptSubmitResponse,
} from '../../interface/examAttempt.interface';
import { IApiErrorResponse, IErrorResponse } from '../../interface/main.interface';
import { examAttemptService } from '../../services/examAttempt';
import { normalizeAxiosError } from '../../utility/axiosErrorHandler';

interface IAttemptArg {
  studentId: string;
  examSubjectId: string;
}

/**
 * CVA-4197 plan 0007 (auto-refresh at exam start): layers `errorCode` onto
 * `normalizeAxiosError`'s result, LOCAL to join/resume only — deliberately
 * not added to the shared `normalizeAxiosError`/`IErrorResponse` used by
 * every other feature's thunks, to keep this addition fully contained.
 * Mirrors `normalizeAxiosError`'s own `errorData` resolution (this repo's
 * `api.tsx` interceptor rejects with the response BODY, not an AxiosError —
 * see this repo's own CLAUDE.md) so it finds `errorCode` regardless of which
 * shape the caught error is.
 */
const withExamAttemptErrorCode = (
  err: unknown,
  base: IErrorResponse,
): IExamAttemptErrorResponse => {
  const error = err as AxiosError<IApiErrorResponse & { errorCode?: string }>;
  const errorData =
    error.response?.data || (error as unknown as IApiErrorResponse & { errorCode?: string });
  return { ...base, errorCode: errorData?.errorCode };
};

/** Join — idempotent server-side, safe to call every time the page mounts. */
export const joinExamAttempt = createAsyncThunk<
  IExamAttemptJoinResponse,
  IAttemptArg,
  { rejectValue: IExamAttemptErrorResponse }
>('examAttempt/join', async ({ studentId, examSubjectId }, { rejectWithValue }) => {
  try {
    const response = await examAttemptService.joinExamAttempt(studentId, examSubjectId);
    return response.data;
  } catch (err: unknown) {
    return rejectWithValue(
      withExamAttemptErrorCode(
        err,
        normalizeAxiosError(err, tr('exam_attempt.error_join', 'Failed to join the exam.')),
      ),
    );
  }
});

/** Resume — same shape as join, used on refresh/re-entry. */
export const resumeExamAttempt = createAsyncThunk<
  IExamAttemptJoinResponse,
  IAttemptArg,
  { rejectValue: IExamAttemptErrorResponse }
>('examAttempt/resume', async ({ studentId, examSubjectId }, { rejectWithValue }) => {
  try {
    const response = await examAttemptService.resumeExamAttempt(studentId, examSubjectId);
    return response.data;
  } catch (err: unknown) {
    return rejectWithValue(
      withExamAttemptErrorCode(
        err,
        normalizeAxiosError(err, tr('exam_attempt.error_resume', 'Failed to resume the exam.')),
      ),
    );
  }
});

/**
 * Autosave one item. `examPaperItemId` rides along in the thunk arg (not just
 * the payload) so the slice can key `saveStatus` by item without re-reading
 * the payload shape in the reducer.
 */
export const autosaveExamAttemptAnswer = createAsyncThunk<
  IExamAttemptAutosaveResponse,
  IAttemptArg & { payload: IAutosaveExamAttemptAnswerPayload },
  { rejectValue: IErrorResponse & { examPaperItemId: string } }
>('examAttempt/autosave', async ({ studentId, examSubjectId, payload }, { rejectWithValue }) => {
  try {
    const response = await examAttemptService.autosaveExamAttemptAnswer(
      studentId,
      examSubjectId,
      payload,
    );
    return response.data;
  } catch (err: unknown) {
    return rejectWithValue({
      ...normalizeAxiosError(err, tr('exam_attempt.error_autosave', 'Failed to save your answer.')),
      examPaperItemId: payload.examPaperItemId,
    });
  }
});

/** Manual submit. Idempotent — a submitted attempt can never be reopened. */
export const submitExamAttempt = createAsyncThunk<
  IExamAttemptSubmitResponse,
  IAttemptArg,
  { rejectValue: IErrorResponse }
>('examAttempt/submit', async ({ studentId, examSubjectId }, { rejectWithValue }) => {
  try {
    const response = await examAttemptService.submitExamAttempt(studentId, examSubjectId);
    return response.data;
  } catch (err: unknown) {
    return rejectWithValue(
      normalizeAxiosError(err, tr('exam_attempt.error_submit', 'Failed to submit the exam.')),
    );
  }
});

export const examAttemptActions = {
  joinExamAttempt,
  resumeExamAttempt,
  autosaveExamAttemptAnswer,
  submitExamAttempt,
};
